import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PeriodoHeader from '../components/PeriodoHeader';
import { usePeriodoComissionamentos } from '../hooks/usePeriodoComissionamentos';
import { Esqueleto } from '../../../components/esqueleto/Esqueleto';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`). O import é explícito de propósito: depender de CSS carregado por
// outra tela é acoplamento invisível — foi o que originou o BUG-V74 na at-67.
import '../../recepcao/pages/Recepcao.css';
import './contratos.css';
// Paleta de selos compartilhada (`cilia-badge` + badge-<cor>) — o selo do boleto que
// deixou de valer usa a mesma do resto do sistema, nunca classe de status própria.
import '../../../shared/ui/badge-status.css';
import DadosAtualizadosEm from '../components/DadosAtualizadosEm';
import AbasDoQualificado from '../components/AbasDoQualificado';

interface LocationState {
  item?: {
    id: string;
    codigo: string;
    nome: string;
    contratos: number;
  };
  origem?: 'cooperativa' | 'voluntario';
  qualificado_tipo?: string;
  qualificado_uuid?: string;
}

interface ContratoItem {
  id: string;
  associado_id: string;
  associado_nome?: string;
  data_pagamento: string;
  valor_pago: number | string;
  mes_referencia: string;
  data_inicio_contrato?: string;
  /**
   * O boleto ainda é um pagamento válido na origem? (at-04, `BUG-007`)
   *
   * `false` = foi pago um dia e não é mais (estorno ou cancelamento). Ele **continua
   * aparecendo na lista**, marcado — some da conta, não da tela. Esconder faria a lista
   * encolher sem explicação, e é justamente conferir um pagamento já feito que traz o
   * usuário a esta tela.
   */
  vale?: boolean;
  motivo_invalidez?: 'ESTORNADO' | 'CANCELADO' | 'FORA_DA_BASE' | null;
  situacao_atualizada_em?: string | null;
}

const ITEMS_PER_PAGE = 25;

const ContratosPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;

  const item = state?.item;
  const origem = state?.origem;
  const [periodo, setPeriodo] = usePeriodoComissionamentos();
  const qualificadoTipo = state?.qualificado_tipo ?? (origem === 'cooperativa' ? 'COOPERATIVA' : 'VOLUNTARIO');
  const qualificadoUuid = state?.qualificado_uuid ?? item?.id;

  const [loading, setLoading] = useState<boolean>(false);
  const [contratos, setContratos] = useState<ContratoItem[]>([]);
  const [totalValor, setTotalValor] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const formatPeriodo = (p: string) => {
    const [mm, yyyy] = p.split('-');
    return `${mm}/${yyyy}`;
  };

  const formatCurrency = (v: number | string | undefined | null): string => {
    const num = typeof v === 'string' ? parseFloat(v) : (v ?? 0);
    if (isNaN(num)) return 'R$ 0,00';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR');
  };

  const handleVoltar = () => {
    navigate('/comissionamentos/listas', { state: { defaultTab: origem } });
  };

  useEffect(() => {
    const fetchContratos = async () => {
      if (!qualificadoUuid || !periodo) return;

      setLoading(true);
      setError(null);

      try {
        const [mm, yyyy] = periodo.split('-');
        const periodoParam = `${mm}-${yyyy}`;

        const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/contratos`;
        const response = await axios.get(url, {
          withCredentials: true,
          params: {
            periodo: periodoParam,
            qualificado_tipo: qualificadoTipo,
            qualificado_uuid: qualificadoUuid,
          },
        });

        const data: ContratoItem[] = response.data?.data ?? [];
        setContratos(data);

        // Garantir que a soma seja numérica.
        // ⚠️ Soma só o que VALE (at-04): o boleto estornado aparece na lista marcado, mas
        // não entra no total — senão o valor da tela discordaria do repasse, que já não o paga.
        const total = data.reduce((acc: number, c: ContratoItem) => {
          if (c.vale === false) return acc;
          const valor = typeof c.valor_pago === 'string' ? parseFloat(c.valor_pago) : (c.valor_pago ?? 0);
          return acc + (isNaN(valor) ? 0 : valor);
        }, 0);
        setTotalValor(total);
        setPage(1); // Reset para primeira página ao recarregar
      } catch (err: any) {
        console.error('Erro ao buscar contratos:', err);
        setError(err?.response?.data?.message || 'Erro ao carregar contratos');
        setContratos([]);
        setTotalValor(0);
      } finally {
        setLoading(false);
      }
    };

    fetchContratos();
  }, [qualificadoUuid, periodo, qualificadoTipo]);

  // Paginação
  const totalPages = Math.ceil(contratos.length / ITEMS_PER_PAGE);
  const paginatedContratos = contratos.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  // A lista mostra todos; a CONTA considera só os que ainda valem (at-04).
  const contratosInvalidos = contratos.filter((c) => c.vale === false).length;
  const contratosValidos = contratos.length - contratosInvalidos;

  if (!item) {
    return (
      <div
        className="Frame1000004012"
        style={{
          height: 'fit-content',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          backgroundColor: 'transparent',
        }}
      >
        {/* Estado de "nenhum item selecionado": mesmo esqueleto, sem cabeçalho — não há
            período nem navegação a oferecer, e o conteúdo exibido é o mesmo de antes. */}
        <div className="ConteDo recepcao-conte-do contratos-conte-do">
          <div className="error-message">
            <h2>Nenhum item selecionado</h2>
            <p>Selecione uma cooperativa ou voluntário na lista para ver os contratos.</p>
            <button onClick={() => navigate('/comissionamentos/listas')} className="btn-voltar">
              Voltar para a lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      {/*
        Esqueleto COMPARTILHADO de página. `recepcao-conte-do` e `recepcao-header-row`
        substituem o contêiner `.contratos-container`, que tinha margens próprias e não
        acompanhava as demais telas. A classe local abaixo guarda só o respiro inferior.
      */}
      <div className="ConteDo recepcao-conte-do contratos-conte-do">
        {/* Header. `contratos-header` ficou só com a faixa laranja de separação — o resto
            do cabeçalho vem do esqueleto compartilhado. */}
        <div className="recepcao-header-row contratos-header">
          <button onClick={handleVoltar} className="btn-back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar
          </button>
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Contratos do Período
          </div>
          {/* Período encostado à direita, como o `flex: 1` do título antigo fazia. */}
          <div style={{ marginLeft: 'auto' }}>
            <PeriodoHeader periodo={periodo} onChange={setPeriodo} />
          </div>
        </div>
        <DadosAtualizadosEm />

        <AbasDoQualificado
          ativa="contratos"
          estado={{ item, origem, qualificado_tipo: qualificadoTipo, qualificado_uuid: qualificadoUuid }}
        />

        {/* Resumo */}
        <div className="item-resumo">
          <div className="resumo-card">
            <h2>Resumo do Período</h2>
            <div className="resumo-dados">
              <div className="dado-item">
                <span className="label">Tipo:</span>
                <span className="valor"><span className="badge">{qualificadoTipo === 'COOPERATIVA' ? 'Cooperativa' : 'Voluntário'}</span></span>
              </div>
              <div className="dado-item">
                <span className="label">Código:</span>
                <span className="valor">{item.codigo}</span>
              </div>
              <div className="dado-item">
                <span className="label">Nome:</span>
                <span className="valor">{item.nome}</span>
              </div>
              <div className="dado-item">
                <span className="label">Período:</span>
                <span className="valor">{formatPeriodo(periodo)}</span>
              </div>
              <div className="dado-item">
                <span className="label">Total de Contratos:</span>
                <span className="valor">
                  {loading ? <Esqueleto largura="50px" /> : contratos.length}
                </span>
              </div>
              <div className="dado-item">
                <span className="label">Valor Total:</span>
                <span className="valor highlight">
                  {loading ? <Esqueleto largura="110px" /> : formatCurrency(totalValor)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de contratos */}
        <div className="contratos-table-container">
          {error && <div className="error-message">{error}</div>}
          {loading ? (
            /* A tabela inteira em esqueleto, com cabeçalho e colunas no lugar — o texto
               solto "Carregando contratos..." colapsava a área e fazia a página saltar. */
            <div className="contratos-table" role="status" aria-busy="true">
              <span className="esqueleto-sr-only">Carregando os contratos do período</span>
              <div className="table-header">
                <div>Data Pagamento</div>
                <div>Associado</div>
                <div>Mês Ref.</div>
                <div>Valor Pago</div>
              </div>
              <div className="table-body">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="table-row" aria-hidden="true">
                    <div className="cell-data"><Esqueleto largura="80px" /></div>
                    <div className="cell-nome"><Esqueleto largura={i % 2 === 0 ? '75%' : '60%'} /></div>
                    <div className="cell-mes"><Esqueleto largura="60px" /></div>
                    <div className="cell-valor"><Esqueleto largura="90px" /></div>
                  </div>
                ))}
              </div>
            </div>
          ) : contratos.length === 0 ? (
            <div className="empty-message">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
              </svg>
              <p>Nenhum contrato encontrado para este período.</p>
            </div>
          ) : (
            <div className="contratos-table">
              <div className="table-header">
                <div>Data Pagamento</div>
                <div>Associado</div>
                <div>Mês Ref.</div>
                <div>Valor Pago</div>
              </div>
              <div className="table-body">
                {paginatedContratos.map((contrato) => {
                  const invalido = contrato.vale === false;
                  return (
                    <div
                      key={contrato.id}
                      className={`table-row ${invalido ? 'contratos-row-invalido' : ''}`}
                    >
                      <div className="cell-data">{formatDate(contrato.data_pagamento)}</div>
                      <div className="cell-nome" title={contrato.associado_nome ?? contrato.associado_id}>
                        {contrato.associado_nome ?? contrato.associado_id}
                        {invalido && (
                          // Selo compartilhado do sistema, nunca classe de status própria.
                          <span className="cilia-badge badge-vermelho contratos-selo">
                            {contrato.motivo_invalidez === 'CANCELADO'
                              ? 'Cancelado'
                              : contrato.motivo_invalidez === 'FORA_DA_BASE'
                                ? 'Fora da base'
                                : 'Pagamento desfeito'}
                          </span>
                        )}
                      </div>
                      <div className="cell-mes">{contrato.mes_referencia}</div>
                      <div className="cell-valor">{formatCurrency(contrato.valor_pago)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="table-footer">
                <div className="footer-info">
                  {/*
                    O contador conta os que VALEM. Sem isso o total da lista discordaria do
                    resumo do período, que já exclui o estornado — e duas telas do sistema
                    respondendo números diferentes para a mesma pergunta é o defeito que a
                    regra de 11/09/2026 proíbe.
                  */}
                  <span className="footer-label">
                    Total: {contratosValidos} {contratosValidos === 1 ? 'contrato' : 'contratos'}
                    {contratosInvalidos > 0 && (
                      <span className="contratos-nota-invalidos">
                        {' '}
                        · {contratosInvalidos}{' '}
                        {contratosInvalidos === 1
                          ? 'não vale mais e ficou fora do cálculo'
                          : 'não valem mais e ficaram fora do cálculo'}
                      </span>
                    )}
                  </span>
                  <span className="footer-valor">{formatCurrency(totalValor)}</span>
                </div>
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      Anterior
                    </button>
                    <span className="pagination-info">Página {page} de {totalPages}</span>
                    <button
                      className="pagination-btn"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContratosPage;
